import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {Subject} from 'rxjs';
import {Router} from '@angular/router';
import {User} from '../_models';
import {AESEncryptDecryptService} from './aesencrypt-decrypt.service';
import {Constants} from '../constants/constants';
import {UtilsService} from './utils.service';

@Injectable()
export class AuthenticationService {
    private readonly loggedInSubject: Subject<boolean>;
    role: String;
    private readonly _authenticatedUser: Subject<User>;


    constructor(private http: HttpClient, private _AESEncryptDecryptService: AESEncryptDecryptService, private router: Router,
                private utilsService: UtilsService) {
        this.loggedInSubject = new Subject();
        this._authenticatedUser = new Subject<User>();
    }

    /**
     * Authenticate against /users/authenticate.
     *
     * Dual-auth: the cashier can paste either an account password OR a 4–8
     * digit clockInPin into the same single-input UX (login screen in
     * device-mode, time-clock dialog, lock-screen). The BE accepts
     * `{username, password?, pin?}` and tries PIN first before falling back
     * to password — so when the caller doesn't know which kind of credential
     * was typed, we forward it as BOTH `password` AND `pin` IF it matches
     * /^\d{4,8}$/. Pure-alpha or longer credentials go through as `password`
     * only (the BE rejects non-numeric PINs at hashClockInPin), keeping
     * password-only callers unchanged.
     *
     * The third argument lets a caller that already knows the credential type
     * (e.g. an explicit "PIN" field) bypass the auto-detect by passing the PIN
     * directly. In that case `password` may be empty.
     */
    login(username: string, password: string, pin?: string) {
        // console.log('In AuthenticateService login'); //*MES*
        const body: any = {username};
        if (pin !== undefined && pin !== null && pin !== '') {
            body.pin = pin;
            if (password) {
                body.password = password;
            }
        } else if (password) {
            body.password = password;
            // Auto-detect: if the single credential field looks like a PIN,
            // forward it under both keys so the BE can fall back to password
            // when the user actually typed a numeric password.
            if (/^\d{4,8}$/.test(password)) {
                body.pin = password;
            }
        }
        return this.http.post<any>(`${environment.apiUrl}/users/authenticate`, body)
            .pipe(map((user: any) => {
                // console.log(`${environment.apiUrl}`);
                // login successful if there's a jwt token in the response
                if (user?.user) {
                    this.role = user.user.role[0];
                    // store user details and jwt token in local storage to keep user logged in between page refreshes
                    this.utilsService.setCurrentUser(user.user, this.role);
                    this._authenticatedUser.next(user.user);
                    this.loggedInSubject.next(true);
                } else if (user?.redirectURL) {
                    return user;
                } else {
                    throw new Error('Invalid Username or Password');
                    return;
                }
                console.log('AuthenticateService login completed'); // *MES*
                return user;
            }));
    }
    cloverLogin(merchantId: string, client_id: string, code: string) {
        let currentUser;
        if (localStorage.getItem('currentUser')) {
            currentUser = this.utilsService.getCurrentUser();
        }
        // Re-auth path: caller is logged in. Send their REAL bearer JWT so
        // the backend can extract `sub` (caller's user id) and pin the
        // session through the OAuth round-trip. Previously this header was
        // the raw orgUuid string — which was not a JWT, jwtDecode produced
        // no payload, the backend fell into its "first Admin on merchantId"
        // path, and the caller silently came back as somebody else.
        if (currentUser?.token) {
            return this.http.post<any>(`${environment.apiUrl}/cloverApi/authenticate`, {
                merchantId: merchantId,
                client_id: client_id,
                code: code
            }, {
                headers: new HttpHeaders().set('Authorization', 'Bearer ' + currentUser.token),
            }).pipe(map(response => {
                    // Re-auth response shape: { ok, orgSettings, merchantId }.
                    // NEVER overwrite the cached user wholesale here — that
                    // was the original cross-account bleed bug. Merge only
                    // orgSettings into the cached user and republish.
                    if (response && response.ok && response.orgSettings) {
                        const merged = {...currentUser, orgSettings: response.orgSettings};
                        this.utilsService.setCurrentUser(merged);
                        this._authenticatedUser.next(merged);
                        this.loggedInSubject.next(true);
                    } else if (response && response.token && response.role) {
                        // Defensive: if the backend regresses to returning a
                        // full user payload on the re-auth path, do NOT
                        // adopt it as the new session — that's the security
                        // bug we just fixed. Drop it on the floor; the
                        // component-level guard will surface the error.
                        console.warn('[CLOVER REAUTH] backend returned full user on re-auth; ignoring');
                    }
                    return response;
                }));
        } else {
            // First-time auth path: caller is unauthenticated. Backend mints
            // a fresh JWT and returns the full user payload — adopt it as
            // the new session. Unchanged from original behavior.
            return this.http.post<any>(`${environment.apiUrl}/cloverApi/authenticate`, {
                merchantId: merchantId,
                client_id: client_id,
                code: code
            }).pipe(map(user => {
                    if (user) {
                        this.utilsService.setCurrentUser(user);
                        this._authenticatedUser.next(user);
                        this.loggedInSubject.next(true);
                    }
                    return user;
                }));
        }
    }

    // cloverLogin(merchantId: string, client_id: string, code: string) {
    //     const currentUser = JSON.parse(this._AESEncryptDecryptService.decrypt(localStorage.
    //         Item('currentUser')));
    //
    //     return this.http.post<any>(`${environment.apiUrl}/cloverApi/authenticate`, {
    //         merchantId: merchantId,
    //         client_id: client_id,
    //         code: code
    //     }, {
    //         headers: new HttpHeaders().set('Authorization', currentUser.orgUuid),
    //     })
    //         .pipe(map(user => {
    //             localStorage.setItem('currentUser', this._AESEncryptDecryptService.encrypt(JSON.stringify(user)));
    //             localStorage.setItem('selectedRole', this._AESEncryptDecryptService.encrypt(user.role[0]));
    //             this._authenticatedUser.next(user);
    //             this.loggedInSubject.next(true);
    //             return user;
    //         }));
    // }


    squareLogin(code: string) {
        if (localStorage.getItem(Constants.CURRENT_USER)) {
            const user: User = this.utilsService.getCurrentUser();
        }
        return this.http.post<any>(`${environment.apiUrl}/squareApi/authenticate`, {code: code})
            .pipe(map(user => {
                if (user) {
                    this.utilsService.setCurrentUser(user);
                    this._authenticatedUser.next(user);
                    this.loggedInSubject.next(true);
                }
                return user;
            }));
    }


    generateOTC(username) {
        return this.http.post<any>(`${environment.apiUrl}/users/generateOTP`, {username});
    }

    verifyOTC(otp, username) {
        return this.http.post<any>(`${environment.apiUrl}/users/verifyOTP`, {otp, username});
    }

    resetPassword(otp, password, userId) {
        return this.http.post<any>(`${environment.apiUrl}/users/resetPassword`, {otp, password, userId});
    }

    logout() {
        // remove user from local storage to log user out
        localStorage.clear();
        sessionStorage.clear();
        this.loggedInSubject.next(false);
        // console.log('in AuthenticateService logout'); //*MES*
        this.router.navigate(['/login']);
    }

    get user() {
        return this._authenticatedUser;
    }

    setUser(user) {
        this._authenticatedUser.next(user);
        this.loggedInSubject.next(true);
    }

    getLoggedInSubject() {
        // console.log('in AuthenticateService getLoggedInSubject'); //*MES*
        return this.loggedInSubject;
    }
}
